// vg-binding: P7.D-148
// vg-binding: P7.D-150
//
// apps/vendor/src/pages/OrderDetail.tsx
//
// Phase 7 Wave 6 Task 20 — vendor order item detail page.

import { Link, useParams } from 'react-router-dom';

import { useTranslation } from '../lib/i18n/index.js';
import { ActionBar } from '../features/orders/detail/ActionBar.js';
import { ArtworkPanel } from '../features/orders/detail/ArtworkPanel.js';
import { OrderInfo } from '../features/orders/detail/OrderInfo.js';
import { ProductionHistoryPanel } from '../features/orders/detail/ProductionHistoryPanel.js';
import { TransitionTimeline } from '../features/orders/detail/TransitionTimeline.js';
import {
  useOrderDetail,
  useOrderTransitions,
  useProductionHistory,
} from '../features/orders/api.js';

export function OrderDetailPage(): JSX.Element {
  const { t } = useTranslation('orders');
  const params = useParams<{ orderId: string; itemId: string }>();
  const detailQ = useOrderDetail(params.orderId, params.itemId);
  const transitionsQ = useOrderTransitions(params.orderId, params.itemId);
  const historyQ = useProductionHistory(params.orderId, params.itemId);

  if (detailQ.isLoading) {
    return (
      <main className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
      </main>
    );
  }

  if (detailQ.isError || !detailQ.data) {
    return (
      <main className="px-6 py-10">
        <div className="mx-auto max-w-md rounded-xl border border-pw-border bg-pw-bg p-6 text-center shadow-sm">
          <h2 className="text-lg font-semibold text-pw-text">
            {t('detail.notFoundTitle')}
          </h2>
          <p className="mt-2 text-sm text-pw-text-muted">
            {t('detail.notFoundBody')}
          </p>
          <Link
            to="/orders"
            className="mt-4 inline-flex rounded-md bg-pw-primary px-4 py-2 text-sm font-medium text-white hover:bg-pw-primary-strong"
          >
            {t('detail.backToList')}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="flex flex-col gap-4 px-6 py-6">
      <header className="flex items-center justify-between">
        <Link
          to="/orders"
          className="text-sm text-pw-primary hover:underline"
        >
          {t('detail.backHeader')}
        </Link>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.5fr_1fr]">
        <div className="flex flex-col gap-4">
          <OrderInfo detail={detailQ.data} />
          <ActionBar
            detail={detailQ.data}
            onMutated={() => {
              void detailQ.refetch();
              void transitionsQ.refetch();
              void historyQ.refetch();
            }}
          />
          <ProductionHistoryPanel
            rows={historyQ.data?.rows ?? []}
            isLoading={historyQ.isLoading}
          />
          <TransitionTimeline
            rows={transitionsQ.data?.rows ?? []}
            isLoading={transitionsQ.isLoading}
          />
        </div>
        <ArtworkPanel files={detailQ.data.artwork_files} />
      </div>
    </main>
  );
}

export default OrderDetailPage;
